import classNames from 'classnames';
import React, { useMemo } from 'react';

import ForwardIcon from '@mui/icons-material/ArrowForwardIos';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from '@mui/styles';

import { translate } from '@vestahealthcare/common/i18n';
import {
  ClinicalMember,
  MemberDashboardProgramTask,
  Patient,
} from '@vestahealthcare/common/models';

import { Card, Colors, Fonts, Tooltip } from 'styleguide-v2';

import Session from 'dash/src/services/SessionServices';

import { RecentProgramTasksTable } from './RecentProgramTasksTable';

type Props = {
  className?: string;
  data?: MemberDashboardProgramTask[];
  member: Patient | ClinicalMember;
};

const useStyles = makeStyles({
  container: {
    '&&': {
      borderRadius: '8px',
    },

    display: 'flex',

    '& > div': {
      flex: 1,
      padding: '2.4rem 3.2rem',
    },
  },
  subContainer: {
    display: 'flex',
    flexFlow: 'column',
  },
  title: {
    alignItems: 'center',
    display: 'flex',

    '&&': {
      fontFamily: Fonts.fontFamily,
      fontSize: `calc(${Fonts.fontSize} * 1.25)`,
      fontWeight: 500,
      margin: 0,
    },
  },
});

export const RecentProgramTasksCard = ({ className, data, member }: Props) => {
  const styles = useStyles();

  const memberId = useMemo(
    () => (member instanceof Patient ? member?.id : member.memberId),
    [member],
  );
  const openNewTab = useMemo(() => !(member instanceof Patient), [member]);
  const shouldShowLink = Session.actingUser.showMemberLink;

  return (
    <Card className={classNames(className, styles.container)}>
      <div className={styles.subContainer}>
        <div className="flex spaced">
          <span className={styles.title}>
            {translate('memberProfile.dashboard.recentCCMTasks')}
            &nbsp;
            <Tooltip
              text={translate('memberProfile.dashboard.recentCCMTasksTooltip')}
            >
              <InfoOutlinedIcon htmlColor={Colors.iconGray} />
            </Tooltip>
          </span>
          {shouldShowLink && (
            <a
              className="flex middle min-gap"
              href={`#/patients/${memberId}/ccm-tasks`}
              style={{ color: Colors.textGreen, fontWeight: 500 }}
              rel="noreferrer"
              target={openNewTab ? '_blank' : undefined}
            >
              {translate('memberProfile.dashboard.goToCCMTasks')}
              <ForwardIcon fontSize="small" />
            </a>
          )}
        </div>
        <br />
        <RecentProgramTasksTable className="flex" data={data} />
      </div>
    </Card>
  );
};
